import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { BooleanInput } from '@angular/cdk/coercion';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { Account } from '@smmof/entity';
import { faLock, faWallet, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { faLightbulb, faNewspaper } from '@fortawesome/free-regular-svg-icons';
import { Store } from '@ngrx/store';
import { AccountState } from '../../../store/reducers/account.reducer';
import { selectAccountState } from '../../../store/selectors/account.selectors';
import { AdOrderItemDraft } from '../../../store/reducers/ad-order-draft.reducer';
import { selectAdOrderItems, selectTotalPrice } from '../../../store/selectors/ad-order-draft.selectors';
import { animate, style, transition, trigger, AnimationEvent, keyframes } from '@angular/animations';

@Component({
  selector: 'user',
  templateUrl: './user.component.html',
  animations: [
    trigger('cartColorChange', [
      transition('inactive => active', [
        animate(
          '500ms ease-in-out',
          keyframes([
            style({
              transform: 'scale(1)',
              backgroundColor: '*', // Используем исходный цвет
              color: '*', // Используем исходный цвет текста
              offset: 0,
            }),
            style({
              transform: 'scale(1.05)',
              backgroundColor: 'rgb(13, 148, 136)', // Новый цвет фона
              color: '#fff', // Новый цвет текста
              offset: 0.5,
            }),
            style({
              transform: 'scale(1)',
              backgroundColor: '*', // Возвращаем к исходному цвету
              color: '*', // Возвращаем к исходному цвету текста
              offset: 1,
            }),
          ])
        ),
      ]),
    ]),
  ],
  styles:  `
    .cart-icon mat-icon {
      transition: color 0.5s ease-in-out;
    }
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'user',
})
export class UserComponent implements OnInit, OnDestroy {
  /* eslint-disable @typescript-eslint/naming-convention */
  static ngAcceptInputType_showAvatar: BooleanInput;
  /* eslint-enable @typescript-eslint/naming-convention */
  faWallet = faWallet;
  faLock = faLock;
  faNewspaper = faNewspaper;
  faLightbulb = faLightbulb;
  faUserPlus = faUserPlus;

  @Input() showAvatar: boolean = true;
  user: Account;

  adOrderItems$: Observable<AdOrderItemDraft[]>;
  totalPrice$: Observable<number>;
  cartAnimationState: 'active' | 'inactive' = 'inactive';
  private previousItemsCount = 0;

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   */
  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _store: Store<{ account: AccountState }>
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this._store
      .select(selectAccountState)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((state) => {
        this.user = state.account;
        // Маркируем для проверки
        this._changeDetectorRef.markForCheck();
      });

    this.adOrderItems$ = this._store.select(selectAdOrderItems).pipe(
      tap((items) => {
        const currentItemsCount = items.length;
        if (currentItemsCount > this.previousItemsCount) {
          this.cartAnimationState = 'active';
          this._changeDetectorRef.markForCheck();
        }
        this.previousItemsCount = currentItemsCount;
      })
    );
    this.totalPrice$ = this._store.select(selectTotalPrice);
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  onAnimationDone(event: AnimationEvent): void {
    if (event.toState === 'active') {
      this.cartAnimationState = 'inactive';
      this._changeDetectorRef.markForCheck();
    }
  }
}
